import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Kaddanse 16.10 (repris de Montrelio 03.10) — même produit, mêmes photos dans le même ordre (ordre fixe) :
        // on GARDE la galerie affichée au lieu de la remplacer. Le remplacement faisait
        // réapparaître la photo 1 une fraction de seconde (« l'image saute ») avant que la
        // nouvelle galerie soit replacée. La position est tenue par kdd-gallery-variant.js
        // (photo de la variante dès le toucher) ; sans ce script, on applique initial-slide.
        const current = this.querySelector('slideshow-component[ref="slideshow"]');
        const incoming = newMediaGallery.querySelector('slideshow-component[ref="slideshow"]');
        if (current && incoming) {
          // Empreinte de la galerie : fichier de chaque photo, dans l'ordre (pas de slide-id ici).
          const ids = (el) =>
            Array.from(el.querySelectorAll('slideshow-slide'), (s) => {
              const m = s.querySelector('img, video, model-viewer');
              const src = (m && (m.getAttribute('src') || m.getAttribute('poster'))) || '';
              return src.split('?')[0];
            }).join(',');
          const fp = ids(current);
          if (fp && !fp.startsWith(',') && fp === ids(incoming)) {
            if (!window.__kddGalleryVariant) {
              const target = incoming.getAttribute('initial-slide') || '0';
              if (current.getAttribute('initial-slide') !== target) current.setAttribute('initial-slide', target);
            }
            return;
          }
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
